import { ReplicationChannel } from './channel'
import {
	holdPresence,
	Locks,
	presenceLockName,
	trackPeer,
} from './peer-presence'
import {
	Address,
	HostBody,
	openReplicaMessage,
	RelayedAction,
	RemoteSlices,
	wrap,
} from './protocol'
import { changedKeys, rememberSent } from './slice-diff'
import { cloneStructured } from './structured-clone'
import {
	projectForReplica,
	relayMatcher,
	StateKey,
	SyncPolicy,
	syncedKeys,
} from './sync-policy'

export type HostStore<S> = {
	getState: () => S
	subscribe: (listener: () => void) => () => void
}

type Options<S> = {
	store: HostStore<S>
	channel: ReplicationChannel
	address: Address
	policy: SyncPolicy<S>
	/** `navigator.locks`; without it the replica is never taken for gone. */
	locks?: Locks
	/** An application message from the replica. */
	onMessage: (payload: unknown) => void
	/** The replica appeared (`true`) or went (`false`). */
	onPeer: (present: boolean) => void
	onOtherBuild: () => void
	/** A slice could not be cloned and is no longer sent. */
	onDropped: (key: string, error: unknown) => void
}

export type ReplicationHost = {
	/** Forwards an action the host's store just applied, if it is relayed. */
	relay: (action: RelayedAction) => void
	/** Sends an application message to the replica. */
	post: (payload: unknown) => void
	stop: () => void
}

const randomId = (): string => Math.random().toString(36).slice(2, 10)

const isCloneError = (error: unknown): boolean =>
	error instanceof DOMException && error.name === 'DataCloneError'

/**
 * The host's end of a link. It speaks once this window holds its presence
 * lock: first a snapshot (a replica already waiting - after a reload of this
 * window - takes it as the new epoch), then every burst of store changes as
 * one `patch` with the slices that are new objects since the last one. A
 * `hello` or `resync` from the replica gets a full snapshot.
 *
 * Changes are flushed in a microtask, not a frame or a timer: browsers
 * throttle those in a hidden window, and the host can be hidden while the
 * replica shows.
 */
export const startHost = <S>({
	store,
	channel,
	address,
	policy,
	locks,
	onMessage,
	onPeer,
	onOtherBuild,
	onDropped,
}: Options<S>): ReplicationHost => {
	const epoch = randomId()
	const isRelayed = relayMatcher(policy)
	const dropped = new Set<StateKey<S>>()
	const peer = trackPeer(
		locks,
		presenceLockName(address.link, 'replica'),
		onPeer
	)
	let rev = 0
	let lastSent: Partial<S> = {}
	let speaking = false
	let scheduled = false
	let stopped = false
	let unsubscribeStore = (): void => undefined
	let unsubscribeChannel = (): void => undefined

	const post = (body: HostBody): void => channel.post(wrap(address, body))

	/**
	 * Posts the slices, dropping for good any that cannot be cloned - a slice
	 * that grew a class instance must not take the rest of the link down.
	 */
	const postData = (kind: 'snapshot' | 'patch', slices: RemoteSlices): void => {
		rev += 1
		try {
			post({ kind, epoch, rev, slices })
		} catch (error) {
			if (!isCloneError(error)) {
				throw error
			}
			const clean: Record<string, unknown> = {}
			Object.keys(slices).forEach(key => {
				try {
					cloneStructured(slices[key])
					clean[key] = slices[key]
				} catch (sliceError) {
					dropped.add(key as StateKey<S>)
					onDropped(key, sliceError)
				}
			})
			post({ kind, epoch, rev, slices: clean })
		}
	}

	const keysFor = (message: 'snapshot' | 'patch'): Array<StateKey<S>> =>
		syncedKeys(policy, message).filter(key => !dropped.has(key))

	const sendSnapshot = (): void => {
		const state = store.getState()
		const keys = keysFor('snapshot')
		postData('snapshot', projectForReplica(state, policy, keys))
		lastSent = rememberSent({}, state, keys)
	}

	const flush = (): void => {
		scheduled = false
		if (stopped) {
			return
		}
		const state = store.getState()
		const keys = changedKeys(lastSent, state, keysFor('patch'))
		if (keys.length) {
			postData('patch', projectForReplica(state, policy, keys))
			lastSent = rememberSent(lastSent, state, keys)
		}
	}

	const presence = holdPresence(
		locks,
		presenceLockName(address.link, 'host'),
		() => {
			if (stopped) {
				return
			}
			speaking = true
			unsubscribeStore = store.subscribe(() => {
				if (!scheduled) {
					scheduled = true
					queueMicrotask(flush)
				}
			})
			unsubscribeChannel = channel.subscribe(data => {
				const message = openReplicaMessage(data, address)
				if (!message.ok) {
					if (message.reason === 'other-build') {
						onOtherBuild()
					}
					return
				}
				peer.heard()
				switch (message.body.kind) {
					case 'hello':
					case 'resync':
						sendSnapshot()
						return
					case 'app':
						onMessage(message.body.payload)
						return
				}
			})
			sendSnapshot()
		}
	)

	return {
		relay: action => {
			if (!speaking || stopped || !isRelayed(action.type)) {
				return
			}
			// Patches already due go first, so the relayed action lands on the
			// state it was applied to on this side.
			flush()
			rev += 1
			post({ kind: 'actions', epoch, rev, actions: [action] })
		},
		post: payload => {
			if (speaking && !stopped) {
				post({ kind: 'app', payload })
			}
		},
		stop: () => {
			stopped = true
			unsubscribeStore()
			unsubscribeChannel()
			peer.stop()
			presence.stop()
		},
	}
}
